class ProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.mainTrack = this.querySelector('[data-carousel-track]');
    this.mainSlides = [...this.querySelectorAll('[data-carousel-slide]')];

    if (!this.mainTrack || typeof EmblaCarousel === 'undefined' || this.mainSlides.length === 0) {
      return;
    }

    this.initialized = true;
    this.controller = new AbortController();
    const { signal } = this.controller;

    const mainViewport = this.mainTrack.parentElement;
    this.mainEmbla = EmblaCarousel(mainViewport, {
      loop: false,
      align: 'start',
      slides: '[data-carousel-slide]:not(.hidden)',
    });

    this.thumbTrack = this.querySelector('[data-carousel-thumb-track]');
    this.thumbButtons = [...this.querySelectorAll('[data-thumbnail-media-id]')];

    if (this.thumbTrack && this.thumbButtons.length > 0) {
      const thumbViewport = this.thumbTrack.parentElement;
      this.thumbEmbla = EmblaCarousel(thumbViewport, {
        loop: false,
        align: 'start',
        containScroll: 'keepSnaps',
        dragFree: true,
        slides: '[data-thumbnail-media-id]:not(.hidden)',
      });

      for (const [index, button] of this.thumbButtons.entries()) {
        button.addEventListener('click', () => this.mainEmbla.scrollTo(index), {
          signal,
        });
      }

      this.mainEmbla.on('select', () => this.syncThumbnails());
      this.syncThumbnails();
    }

    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.prevButton?.addEventListener('click', () => this.mainEmbla.scrollPrev(), { signal });
    this.nextButton?.addEventListener('click', () => this.mainEmbla.scrollNext(), { signal });

    const updateNavButtons = () => {
      const canPrev = this.mainEmbla.canScrollPrev();
      const canNext = this.mainEmbla.canScrollNext();
      this.prevButton?.classList.toggle('hidden', !canPrev);
      this.prevButton?.classList.toggle('flex', canPrev);
      this.nextButton?.classList.toggle('hidden', !canNext);
      this.nextButton?.classList.toggle('flex', canNext);
    };
    this.mainEmbla.on('select', updateNavButtons);
    this.mainEmbla.on('reInit', updateNavButtons);
    updateNavButtons();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.mainEmbla?.destroy();
    this.thumbEmbla?.destroy();
    this.mainEmbla = undefined;
    this.thumbEmbla = undefined;
    this.initialized = false;
  }

  syncThumbnails() {
    if (!this.mainEmbla || !this.thumbEmbla) return;

    const selected = this.mainEmbla.selectedScrollSnap();
    this.thumbEmbla.scrollTo(selected);

    for (const [index, button] of this.thumbButtons.entries()) {
      button.setAttribute('aria-current', index === selected ? 'true' : 'false');
    }
  }

  // Desktop mosaic: show the large tile for this media, falling back to the first one.
  showHeroMedia(mediaId) {
    const heroes = [...this.querySelectorAll('[data-gallery-hero]')];
    if (heroes.length < 2) return;

    const target = heroes.find((hero) => Number(hero.dataset.galleryHero) === Number(mediaId)) ?? heroes[0];
    for (const hero of heroes) {
      hero.classList.toggle('hidden', hero !== target);
    }
  }

  // Turn a hidden large tile's lazy image eager so it is ready before it is shown.
  preloadHeroMedia(mediaId) {
    const image = this.querySelector(`[data-gallery-hero="${Number(mediaId)}"] img[loading="lazy"]`);
    if (image) image.loading = 'eager';
  }

  scrollToMediaId(mediaId) {
    if (!this.mainEmbla) return;

    const visibleSlides = this.mainSlides.filter((slide) => !slide.classList.contains('hidden'));
    const index = visibleSlides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(mediaId));
    if (index === -1) return;

    this.mainEmbla.scrollTo(index);
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
